import * as React from 'react';
import {
  UnknownPlaceholders,
  TranslationFunction,
  TranslationNodeFunction,
} from '../types';

const PLACEHOLDER_REGEX = /[{][{](.*?)(?:!(.+?))?[}][}]/g;

export default function (
  t: TranslationFunction,
  shouldShowTransifyKey: boolean
): TranslationNodeFunction {
  return function (key: string, opts?: UnknownPlaceholders): React.ReactNode {
    if (shouldShowTransifyKey) {
      return key;
    }
    // example: i18n.tNode('label_yes', { test: <a href="#">Hello</a> })
    const translatedString = t(key, {
      context: typeof opts?.context === 'string' ? opts.context : '',
    });
    // translatedString = yes {{test}}
    const reactNodeArray = splitByPlaceholderAndReplace(
      PLACEHOLDER_REGEX,
      translatedString,
      opts
    );
    // reactNodeArray = ['yes ', React.Element<'a'>]
    return joinReactNode(reactNodeArray);
    // yes <a href="#">Hello</a>
  };
}

function joinReactNode(strArray: React.ReactNode[]): React.ReactNode {
  return strArray.map(function (textOrComponent, index) {
    if (typeof textOrComponent === 'string') {
      return textOrComponent;
    } else if (textOrComponent && React.isValidElement(textOrComponent)) {
      return React.cloneElement(textOrComponent, { key: index });
    } else {
      throw Error('Only String or valid React Element could be passed');
    }
  });
}

function isRawStringOrNumber(obj: unknown): boolean {
  const type = typeof obj;
  return type === 'string' || type === 'number';
}

function mergeRawStrings(
  mergedArray: React.ReactNode[],
  currentElement: React.ReactNode
): React.ReactNode[] {
  const lastIndex = mergedArray.length - 1;
  const lastMergedElement = mergedArray[lastIndex];
  if (
    isRawStringOrNumber(lastMergedElement) &&
    isRawStringOrNumber(currentElement)
  ) {
    return [
      ...mergedArray.slice(0, lastIndex),
      String(lastMergedElement) + String(currentElement),
    ];
  } else if (currentElement) {
    return [...mergedArray, currentElement];
  }
  return mergedArray;
}

function splitByPlaceholderAndReplace(
  regex: RegExp,
  str: string,
  data?: UnknownPlaceholders
): React.ReactNode[] {
  const templateDetection = new RegExp(regex);
  const aggregatorReactNode: React.ReactNode[] = [];
  let match;
  let cursor = 0;
  while ((match = templateDetection.exec(str))) {
    const [literal, key] = match;
    const { index } = match;
    const prevPart = str.substring(cursor, index);
    if (prevPart) {
      aggregatorReactNode.push(prevPart);
    }
    // Replace placeholders
    const replacement = data ? data[key] : null;
    if (replacement) {
      aggregatorReactNode.push(replacement as React.ReactNode);
    }
    cursor = index + literal.length;
  }
  const nextPart = str.substring(cursor, str.length);
  if (nextPart) {
    aggregatorReactNode.push(nextPart);
  }
  return aggregatorReactNode.reduce(mergeRawStrings, []);
}
